import { BaseCatalogItemTransformer } from './baseCatalogItem.transformer';
import type { Installation } from '@/types';
import type { CatalogType } from '../services/catalog/catalog-types';
import { firstMediaData, playbackUrl } from '@/lib/utils/media.utils';
import { processTagArray, getYearTag, truncateToWords, combineTags } from '@/lib/utils/tagGenerator';

/**
 * Transformer for converting Installation entities to CatalogItem
 */
export class InstallationTransformer extends BaseCatalogItemTransformer<Installation> {
  protected getId(installation: Installation): string | number {
    return installation.id;
  }

  protected getType(_installation: Installation): CatalogType {
    return 'installation';
  }

  protected getSlug(installation: Installation): string {
    return installation.slug;
  }

  protected getHref(installation: Installation): string {
    return `/catalog/immersive/${installation.slug}`;
  }

  protected getTitle(installation: Installation): string {
    return installation.title;
  }

  protected getDisplayTitle(installation: Installation): string {
    return installation.title;
  }

  protected getFilename(installation: Installation): string {
    return installation.slug;
  }

  protected getThumbnail(installation: Installation): string {
    return firstMediaData(installation.cardImage, installation.coverImage, installation.meta?.image).url || '';
  }

  protected getImageUrl(installation: Installation): string {
    return firstMediaData(installation.coverImage, installation.cardImage, installation.meta?.image).url || '';
  }

  protected getWidth(installation: Installation): number {
    return firstMediaData(installation.cardImage, installation.coverImage, installation.meta?.image).width || 800;
  }

  protected getHeight(installation: Installation): number {
    return firstMediaData(installation.cardImage, installation.coverImage, installation.meta?.image).height || 600;
  }

  protected getDate(installation: Installation): string {
    return installation.createdAt;
  }

  protected getYear(installation: Installation): number {
    return installation.year || new Date(installation.createdAt).getFullYear();
  }

  protected getDescription(installation: Installation): string {
    return installation.description;
  }

  protected getLongDescription(installation: Installation): string {
    return installation.description;
  }

  protected getMedium(installation: Installation): string | undefined {
    return installation.medium || undefined;
  }

  protected getDimensions(installation: Installation): string | undefined {
    return installation.dimensions || undefined;
  }

  protected getLocation(installation: Installation): string | undefined {
    return installation.location || undefined;
  }

  protected getVideoUrl(installation: Installation): string | undefined {
    return playbackUrl(installation.video) || undefined;
  }

  protected getTags(installation: Installation): string[] {
    return combineTags(
      processTagArray(installation.tags),
      getYearTag(installation.createdAt),
      truncateToWords(installation.medium, 3)
    );
  }
}

// Export singleton instance
export const installationTransformer = new InstallationTransformer();